import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@material-ui/core/styles';
import { IncidentFilter, FilterableColumn } from './types';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  chip: {
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#E6F3FF',
    },
    '& .MuiChip-label': {
      color: '#2B6CB0',
      fontWeight: 500,
    },
    '& .MuiChip-deleteIcon': {
      color: '#3182CE',
      '&:hover': {
        color: '#2B6CB0',
      },
    },
  },
  editableChip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    backgroundColor: '#EBF8FF',
    border: '1px solid #3182CE',
    borderRadius: 16,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  columnSelect: {
    minWidth: 160,
    '& .MuiInputBase-root': {
      fontSize: 13,
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CBD5E0',
    },
  },
  valueSelect: {
    minWidth: 200,
    '& .MuiInputBase-root': {
      fontSize: 13,
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CBD5E0',
    },
  },
  closeButton: {
    padding: 2,
    color: '#4A5568',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
    },
  },
  closeIcon: {
    fontSize: 16,
  },
  addButton: {
    backgroundColor: '#F7FAFC',
    border: '1px dashed #CBD5E0',
    borderRadius: 16,
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: '#EDF2F7',
      borderColor: '#A0AEC0',
    },
  },
  addIcon: {
    fontSize: 18,
    color: '#4A5568',
  },
  valueChip: {
    marginRight: 2,
    marginBottom: 2,
    height: 22,
    fontSize: 12,
  },
  dropdownOption: {
    padding: theme.spacing(0.75, 1.5),
    margin: theme.spacing(0.25, 0.5),
    borderRadius: 4,
    fontSize: 13,
    '&:hover': {
      backgroundColor: '#EDF2F7',
    },
    '&[aria-selected="true"]': {
      backgroundColor: '#EBF8FF',
    },
  },
  dropdownPaper: {
    marginTop: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    border: '1px solid #E2E8F0',
    borderRadius: 6,
  },
  dropdownListbox: {
    padding: theme.spacing(0.5, 0),
  },
  isNotToggle: {
    padding: theme.spacing(0.25, 0.75),
    fontSize: 12,
    fontWeight: 500,
    color: '#4A5568',
    backgroundColor: '#F7FAFC',
    border: '1px solid #CBD5E0',
    borderRadius: 4,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#EDF2F7',
    },
  },
  isNotToggleActive: {
    color: '#C53030',
    backgroundColor: '#FED7D7',
    borderColor: '#FC8181',
    '&:hover': {
      backgroundColor: '#FEB2B2',
    },
  },
}));

interface EditableFilterProps {
  filter: IncidentFilter | null;
  availableColumns: FilterableColumn[];
  getAvailableValuesForColumn: (column: FilterableColumn) => string[];
  onSave: (column: FilterableColumn, values: string[], isNot: boolean) => void;
  onCancel: () => void;
  onRemove?: () => void;
  isNew: boolean;
}

const EditableFilter: React.FC<EditableFilterProps> = ({
  filter,
  availableColumns,
  getAvailableValuesForColumn,
  onSave,
  onCancel,
  onRemove,
  isNew,
}) => {
  const classes = useStyles();
  const [selectedColumn, setSelectedColumn] = useState<FilterableColumn | null>(
    filter?.column || null
  );
  const [selectedValues, setSelectedValues] = useState<string[]>(filter?.values || []);
  const [isNot, setIsNot] = useState<boolean>(filter?.isNot || false);
  const containerRef = useRef<HTMLDivElement>(null);

  const formatColumnLabel = (column: string): string => {
    return column.replace(/\n/g, ' ').replace(/\s*\([Cc]alculated\)/g, '');
  };

  const handleColumnChange = (column: FilterableColumn | null) => {
    setSelectedColumn(column);
    if (column !== filter?.column) {
      setSelectedValues([]);
    }
  };

  const handleValuesChange = (values: string[]) => {
    setSelectedValues(values);
    if (selectedColumn && values.length > 0) {
      onSave(selectedColumn, values, isNot);
    }
  };

  const handleIsNotToggle = () => {
    const newIsNot = !isNot;
    setIsNot(newIsNot);
    if (selectedColumn && selectedValues.length > 0) {
      onSave(selectedColumn, selectedValues, newIsNot);
    }
  };

  const handleClickAway = () => {
    if (selectedColumn && selectedValues.length > 0) {
      onSave(selectedColumn, selectedValues, isNot);
    } else if (isNew) {
      onCancel();
    } else if (filter) {
      onSave(filter.column, filter.values, filter.isNot || false);
    }
  };

  const columnsToShow = isNew
    ? availableColumns
    : filter
      ? [filter.column, ...availableColumns.filter((c) => c !== filter.column)]
      : availableColumns;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Paper className={classes.editableChip} ref={containerRef} elevation={0}>
        <Autocomplete
          size="small"
          className={classes.columnSelect}
          options={columnsToShow}
          value={selectedColumn}
          onChange={(_, value) => handleColumnChange(value)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Field" variant="outlined" size="small" />
          )}
          getOptionLabel={(option) => formatColumnLabel(option)}
          renderOption={(props, option) => (
            <li {...props} className={classes.dropdownOption}>
              {formatColumnLabel(option)}
            </li>
          )}
          slotProps={{
            paper: { className: classes.dropdownPaper },
          }}
          ListboxProps={{ className: classes.dropdownListbox }}
          disableClearable={!isNew}
          openOnFocus
          autoHighlight
        />

        {selectedColumn && (
          <Box
            component="span"
            className={`${classes.isNotToggle} ${isNot ? classes.isNotToggleActive : ''}`}
            onClick={handleIsNotToggle}
          >
            {isNot ? 'is not' : 'is'}
          </Box>
        )}

        {selectedColumn && (
          <Autocomplete
            multiple
            size="small"
            className={classes.valueSelect}
            options={getAvailableValuesForColumn(selectedColumn)}
            value={selectedValues}
            onChange={(_, values) => handleValuesChange(values)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Values" variant="outlined" size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props} className={classes.dropdownOption}>
                {option}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  className={classes.valueChip}
                />
              ))
            }
            slotProps={{
              paper: { className: classes.dropdownPaper },
            }}
            ListboxProps={{ className: classes.dropdownListbox }}
            limitTags={2}
            openOnFocus
            autoHighlight
          />
        )}

        <IconButton
          className={classes.closeButton}
          size="small"
          onClick={() => {
            if (onRemove && !isNew) {
              onRemove();
            } else {
              onCancel();
            }
          }}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </Paper>
    </ClickAwayListener>
  );
};

interface FilterChipsProps {
  filters: IncidentFilter[];
  availableValues: Record<FilterableColumn, string[]>;
  getAvailableValuesForColumn: (column: FilterableColumn) => string[];
  onRemoveFilter: (column: FilterableColumn) => void;
  onAddFilter: (column: FilterableColumn, values: string[], isNot?: boolean) => void;
}

export const FilterChips: React.FC<FilterChipsProps> = ({
  filters,
  availableValues,
  getAvailableValuesForColumn,
  onRemoveFilter,
  onAddFilter,
}) => {
  const classes = useStyles();
  const [editingColumn, setEditingColumn] = useState<FilterableColumn | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const formatFilterLabel = (filter: IncidentFilter): string => {
    const columnLabel = filter.column.replace(/\n/g, ' ').replace(/\s*\([Cc]alculated\)/g, '');
    const operator = filter.isNot ? '≠' : '=';
    if (filter.values.length === 1) {
      return `${columnLabel} ${operator} ${filter.values[0]}`;
    }
    return `${columnLabel} ${operator} (${filter.values.length})`;
  };

  const availableColumns = Object.keys(availableValues).filter(
    (col) =>
      !filters.some((f) => f.column === col) && availableValues[col as FilterableColumn].length > 0
  ) as FilterableColumn[];

  const handleChipClick = (column: FilterableColumn) => {
    setEditingColumn(column);
    setIsAddingNew(false);
  };

  const handleSaveFilter = (column: FilterableColumn, values: string[], isNot: boolean) => {
    onAddFilter(column, values, isNot);
    setEditingColumn(null);
    setIsAddingNew(false);
  };

  const handleCancelEdit = () => {
    setEditingColumn(null);
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingColumn(null);
  };

  return (
    <Box className={classes.container}>
      {filters.map((filter) =>
        editingColumn === filter.column ? (
          <EditableFilter
            key={filter.column}
            filter={filter}
            availableColumns={availableColumns}
            getAvailableValuesForColumn={getAvailableValuesForColumn}
            onSave={handleSaveFilter}
            onCancel={handleCancelEdit}
            onRemove={() => onRemoveFilter(filter.column)}
            isNew={false}
          />
        ) : (
          <Tooltip key={filter.column} title={filter.values.join(', ')}>
            <Chip
              className={classes.chip}
              label={formatFilterLabel(filter)}
              onClick={() => handleChipClick(filter.column)}
              onDelete={() => onRemoveFilter(filter.column)}
              size="small"
            />
          </Tooltip>
        )
      )}

      {isAddingNew && (
        <EditableFilter
          filter={null}
          availableColumns={availableColumns}
          getAvailableValuesForColumn={getAvailableValuesForColumn}
          onSave={handleSaveFilter}
          onCancel={handleCancelEdit}
          isNew
        />
      )}

      {!isAddingNew && availableColumns.length > 0 && (
        <Tooltip title="Add filter">
          <IconButton className={classes.addButton} size="small" onClick={handleAddNew}>
            <AddIcon className={classes.addIcon} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default FilterChips;
